import { Ticket, Users, Store, Scan, BarChart3, Lightbulb, ShieldAlert, FileCheck2, Sparkles } from "lucide-react";
import type { ArticleContent, ArticleTile } from "./types";
import { Hero, Section, Steps, Bullets, Tip } from "./renderHelpers";

const CATEGORY = "Events";

export const tiles: ArticleTile[] = [
  { slug: "selling-event-tickets", title: "Selling Event Tickets", category: CATEGORY, description: "Set up paid or free tickets for any event", icon: Ticket },
  { slug: "volunteer-signups", title: "Volunteer Signups & Printable Sheets", category: CATEGORY, description: "Recruit helpers and print signup forms", icon: Users },
  { slug: "stall-applications", title: "Stall Applications & Assignments", category: CATEGORY, description: "Manage external stall holders for fairs", icon: Store },
  { slug: "event-checkin", title: "Event Check-in on the Day", category: CATEGORY, description: "Scan tickets and track attendance", icon: Scan },
  { slug: "event-reports", title: "Generating Event Reports", category: CATEGORY, description: "Branded PDFs summarising every event", icon: BarChart3 },
  { slug: "risk-assessments-overview", title: "Risk Assessments Overview", category: CATEGORY, description: "Why every event needs one and how to start", icon: ShieldAlert },
  { slug: "creating-risk-assessment", title: "Creating a Risk Assessment", category: CATEGORY, description: "Use templates or build from scratch with the 5×5 matrix", icon: FileCheck2 },
  { slug: "ra-ai-suggestions", title: "AI Hazard Suggestions", category: CATEGORY, description: "Let AI pre-fill common hazards for your activity", icon: Sparkles },
  { slug: "ra-approval-workflow", title: "Approval & Version History", category: CATEGORY, description: "Lock approved RAs and track edits as new versions", icon: FileCheck2 },
  { slug: "ra-export-share", title: "Exporting & Sharing Risk Assessments", category: CATEGORY, description: "Download as PDF or Word for committee and helpers", icon: BarChart3 },
];

export const articles: Record<string, ArticleContent> = {
  "selling-event-tickets": {
    title: "Selling Event Tickets",
    category: CATEGORY,
    readTime: "4 min read",
    content: (
      <div className="space-y-6">
        <Hero>Sell tickets through your fundraising page using your connected payment provider. Buyers receive a confirmation email with a QR code.</Hero>
        <Section icon={Ticket} title="Set up tickets">
          <Steps items={[
            <>Open the event in <strong>Events Dashboard</strong>.</>,
            <>Scroll to <strong>Tickets</strong> and click <strong>Add Ticket Type</strong>.</>,
            <>Set name, price, available quantity and per-person limit.</>,
            <>Publish — tickets appear on your public event page.</>,
          ]} />
        </Section>
        <Tip>Ticketing requires the Standard plan and an active payment provider (Stripe or Mollie).</Tip>
      </div>
    ),
  },
  "volunteer-signups": {
    title: "Volunteer Signups & Printable Sheets",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Recruit helpers online or print a paper signup sheet for the school gate.</Hero>
        <Section icon={Users} title="Open volunteer signups">
          <Steps items={[
            <>Open the event and go to the <strong>Volunteers</strong> tab.</>,
            <>Add roles needed (e.g. 'Cake Stall — 2 people, 2-4pm').</>,
            <>Share the signup link with parents.</>,
          ]} />
        </Section>
        <Section icon={Lightbulb} title="Printable signup PDF">
          <Bullets items={[
            "Click Print Signup Sheet on any event.",
            "PDF lists Name and Role rows only — perfect for clipboards.",
            "Use it offline and add signups manually later.",
          ]} />
        </Section>
      </div>
    ),
  },
  "stall-applications": {
    title: "Stall Applications & Assignments",
    category: CATEGORY,
    readTime: "4 min read",
    content: (
      <div className="space-y-6">
        <Hero>Let external stall holders apply to your fair through a public form, then assign them in bulk from the Events tab.</Hero>
        <Section icon={Store} title="Open the application form">
          <Steps items={[
            <>In the event, enable <strong>Accept Stall Applications</strong>.</>,
            <>Set fee, available pitches and any rules.</>,
            <>Share the application link with local businesses.</>,
          ]} />
        </Section>
        <Section icon={Lightbulb} title="Assign stalls">
          <Bullets items={[
            "Go to Events → your event → Stalls tab.",
            "Select multiple applications and assign in bulk.",
            "Approved stall holders are emailed payment instructions automatically.",
          ]} />
        </Section>
      </div>
    ),
  },
  "event-checkin": {
    title: "Event Check-in on the Day",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Use the check-in page on a phone or tablet to scan ticket QR codes as guests arrive.</Hero>
        <Section icon={Scan} title="Open check-in mode">
          <Steps items={[
            <>Open your event in <strong>Events Dashboard</strong>.</>,
            <>Click <strong>Check-in Mode</strong>.</>,
            <>Allow camera access and scan tickets, or search by name.</>,
          ]} />
        </Section>
        <Tip>Multiple committee members can check guests in at the same time on different devices.</Tip>
      </div>
    ),
  },
  "event-reports": {
    title: "Generating Event Reports",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>After every event, generate a branded PDF report covering tickets sold, income, expenses, profit and helper attendance.</Hero>
        <Section icon={BarChart3} title="Generate a report">
          <Steps items={[
            <>Open the event and click <strong>Generate Report</strong>.</>,
            <>Review the AI-drafted summary (uses registered helpers and recorded income).</>,
            <>Edit if needed, then download as PDF or email to committee.</>,
          ]} />
        </Section>
        <Tip>Reports are great for AGM packs and trustee reviews — keep them on file.</Tip>
      </div>
    ),
  },
  "risk-assessments-overview": {
    title: "Risk Assessments Overview",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Every PTA event — from a cake sale to a summer fair — should have a documented risk assessment. The Events Dashboard includes a full RA tool with templates, interactive scoring and approval workflow.</Hero>
        <Section icon={ShieldAlert} title="Why it matters">
          <Bullets items={[
            "Demonstrates duty of care to volunteers, attendees and the school.",
            "Required by most schools and venue insurers.",
            "Helps the committee plan controls before the day.",
          ]} />
        </Section>
        <Section icon={Lightbulb} title="Where to find it">
          <Steps items={[
            <>Go to <strong>Events Dashboard</strong>.</>,
            <>Open the <strong>Risk Assessments</strong> tab.</>,
            <>Start from a template, duplicate an existing RA, or create one from scratch.</>,
          ]} />
        </Section>
        <Tip>Available on trial and all paid plans. Only committee members can view risk assessments — they are never shown publicly.</Tip>
      </div>
    ),
  },
  "creating-risk-assessment": {
    title: "Creating a Risk Assessment",
    category: CATEGORY,
    readTime: "5 min read",
    content: (
      <div className="space-y-6">
        <Hero>Build a risk assessment using the interactive 5×5 matrix. Each hazard is scored on Likelihood × Severity, with colour-coded bands so high risks stand out.</Hero>
        <Section icon={FileCheck2} title="Add hazards">
          <Steps items={[
            <>Enter a <strong>title</strong>, <strong>activity type</strong>, <strong>location</strong> and <strong>review date</strong>.</>,
            <>For each hazard add: hazard, who is at risk, existing controls, likelihood (1–5) and severity (1–5).</>,
            <>Add residual scores after extra controls and assign an action owner.</>,
          ]} />
        </Section>
        <Section icon={Lightbulb} title="Scoring bands">
          <Bullets items={[
            "1–6 Low (green) — acceptable with normal controls.",
            "8–12 Medium (amber) — additional controls recommended.",
            "15–25 High (red) — action required before the event goes ahead.",
          ]} />
        </Section>
        <Tip>Use one of the 12+ ready-made templates (Summer Fair, BBQ, Disco, Bouncy Castle…) to save time.</Tip>
      </div>
    ),
  },
  "ra-ai-suggestions": {
    title: "AI Hazard Suggestions",
    category: CATEGORY,
    readTime: "2 min read",
    content: (
      <div className="space-y-6">
        <Hero>Stuck on what to include? Let AI suggest 5–8 common hazards for your specific activity.</Hero>
        <Section icon={Sparkles} title="How to use it">
          <Steps items={[
            <>Inside any risk assessment, click <strong>AI Suggest Hazards</strong>.</>,
            <>The activity title and type are sent to our AI assistant.</>,
            <>Review, edit or remove any suggestions before saving — you stay in control.</>,
          ]} />
        </Section>
        <Tip>AI suggestions are a starting point, not a substitute for committee judgment. Always tailor controls to your venue and helpers.</Tip>
      </div>
    ),
  },
  "ra-approval-workflow": {
    title: "Approval & Version History",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Once a risk assessment is ready, send it for approval. Only the <strong>Chair</strong> or a <strong>Trustee</strong> can approve, locking the RA at that version.</Hero>
        <Section icon={FileCheck2} title="Workflow">
          <Steps items={[
            <>Author marks the RA as <strong>Pending Approval</strong>.</>,
            <>Chair or Trustee reviews and clicks <strong>Approve</strong>.</>,
            <>The RA is locked and stamped with approver name, role and date.</>,
            <>Any subsequent edit creates a new version — the approved version is preserved.</>,
          ]} />
        </Section>
        <Tip>Set a review date so the RA appears as <em>needs review</em> when it expires.</Tip>
      </div>
    ),
  },
  "ra-export-share": {
    title: "Exporting & Sharing Risk Assessments",
    category: CATEGORY,
    readTime: "2 min read",
    content: (
      <div className="space-y-6">
        <Hero>Download any risk assessment as a branded PDF or Word document — perfect for sharing with the school, venue or insurer.</Hero>
        <Section icon={BarChart3} title="Export">
          <Bullets items={[
            "Click the PDF icon to download a printable PDF with PTA branding.",
            "Click the Word icon to download an editable .doc file.",
            "Helpers signing up online tick an acknowledgement that an RA is in place.",
          ]} />
        </Section>
        <Tip>Approved RAs show an <strong>RA: Approved</strong> chip next to the event in the Events Dashboard.</Tip>
      </div>
    ),
  },
};
